import useSWR from 'swr'

import { options, propertyApi, tokenOptions } from '../../apis/suitebookClient'

export type HotelPlansOrder = 'price' | '-price' | 'recommendation'

export type UseHotelPlansApiProps = {
  propertySlug: string
  checkInDate?: string
  checkOutDate?: string
  roomCount?: number
  numberOfAdults?: number
  numberOfChildren?: number
  numberOfInfants?: number
  // `showAllPlans=false` returns only reservable plans; `order` sorts the list
  // (parity with the room-scoped `usePlansApi`).
  showAllPlans?: boolean
  order?: HotelPlansOrder
  accessToken?: string
}

/**
 * Fetches the facility's public plan list (no room scope) for the plan-first
 * landing page (`/:property/plans`). Each plan carries a single representative
 * price (the cheapest reservable listing); secret plans are excluded by the
 * backend. Price / availability are always evaluated (missing search params
 * fall back to server defaults), mirroring `usePlanWithRoomsApi`.
 *
 * See docs/specs/2026-06-05-property-plans-index-design.md §4.2.
 */
export const useHotelPlansApi = ({
  propertySlug,
  checkInDate,
  checkOutDate,
  roomCount,
  numberOfAdults,
  numberOfChildren,
  numberOfInfants,
  showAllPlans,
  order,
  accessToken,
}: UseHotelPlansApiProps) => {
  const { data, isLoading, isValidating, error, mutate } = useSWR(
    // Gate on `propertySlug` so an empty slug doesn't fire a request to
    // `/hotels//plans`. Mirrors `usePlanWithRoomsApi`.
    propertySlug
      ? ([
          'hotel-plans',
          propertySlug,
          checkInDate ?? null,
          checkOutDate ?? null,
          roomCount ?? null,
          numberOfAdults ?? null,
          numberOfChildren ?? null,
          numberOfInfants ?? null,
          showAllPlans ?? null,
          order ?? null,
        ] as const)
      : null,
    propertySlug
      ? () =>
          propertyApi
            .getHotelPlans(
              propertySlug,
              checkInDate,
              checkOutDate,
              roomCount,
              numberOfAdults,
              numberOfChildren,
              numberOfInfants,
              showAllPlans,
              order,
              accessToken ? tokenOptions(accessToken) : options
            )
            .then((res) => res.data)
      : () => undefined,
    {
      revalidateOnFocus: false,
    }
  )

  return { data, isLoading, isValidating, error, mutate }
}
